/* eslint-disable react/prop-types */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Callout, Library, LibraryMutations, Paragraph, Title } from '@treams/treams-ui';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';

import FeedbackQuestionCard from './FeedbackQuestionCard';

const Container = styled(Box)`
	height: 100%;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

export function FeedbackLibrary(props: {
	inline?: boolean;
	archived?: boolean;
	readOnly?: boolean;
	selectedQuestions?: graphql.FeedbackQuestion[];
	onChangeSelectedQuestions?: (questions: graphql.FeedbackQuestion[]) => void;
}) {
	const { t } = useTranslation();
	const readOnly = props.readOnly;

	const query = graphql.useFeedbackLibraryQuery({
		fetchPolicy: 'network-only',
		variables: { isArchived: props.archived ?? false },
	});

	const categories = query.data?.feedbackQuestionCategories.edges.map(
		({ node }) => node as graphql.FeedbackQuestionCategory,
	);

	const isUncategorized = (question) => {
		return (
			question.isArchived && !categories?.find((category) => category.id === question.category.id)
		);
	};

	const questions = query.data?.feedbackQuestions.edges
		.map(({ node }) => node as graphql.FeedbackQuestion)
		.map((question) => {
			if (isUncategorized(question)) {
				return {
					...question,
					category: { id: '$uncategorized' } as graphql.FeedbackQuestionCategory,
				};
			}
			return question;
		});

	if (questions?.find((question) => question.category.id === '$uncategorized')) {
		categories?.push({
			name: [{ languageID: 'en_US', text: t('common.uncategorized') }],
			id: '$uncategorized',
			order: 0,
			isArchived: true,
		});
	}

	const [createCategory] = graphql.useCreateFeedbackQuestionCategoryMutation();
	const [archiveCategory] = graphql.useArchiveFeedbackQuestionCategoryMutation();
	const [restoreCategory] = graphql.useRestoreFeedbackQuestionCategoryMutation();
	const [reorderCategory] = graphql.useReorderFeedbackQuestionCategoryMutation();
	const [updateCategory] = graphql.useUpdateFeedbackQuestionCategoryMutation();
	const [reorderQuestion] = graphql.useReorderFeedbackQuestionMutation();

	const [archive] = graphql.useArchiveFeedbackQuestionMutation();
	const [restore] = graphql.useRestoreFeedbackQuestionMutation();

	const onArchive = async (item: graphql.FeedbackQuestion) => {
		if (item.isArchived) {
			await restore({ variables: { input: { id: item.id } } });
			return;
		}
		await archive({ variables: { input: { id: item.id } } });
	};

	const mutations = useMemo<
		LibraryMutations<graphql.FeedbackQuestionCategory, graphql.FeedbackQuestion>
	>(
		() =>
			readOnly
				? {}
				: {
						createCategory: async (name) => {
							const response = await createCategory({ variables: { input: { name } } });
							return response.data!.createFeedbackQuestionCategory
								.feedbackQuestionCategory as graphql.FeedbackQuestionCategory;
						},
						reorderCategory: async (id, order) => {
							const response = await reorderCategory({ variables: { input: { id, order } } });
							return response.data!.reorderFeedbackQuestionCategory
								.feedbackQuestionCategory as graphql.FeedbackQuestionCategory;
						},
						reorderItem: async (id, categoryID, order) => {
							const response = await reorderQuestion({
								variables: { input: { id, categoryID, order } },
							});
							return response.data!.reorderFeedbackQuestion
								.feedbackQuestion as graphql.FeedbackQuestion;
						},
				  },
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[readOnly],
	);

	const selectedQuestionIDs = props.selectedQuestions?.map(({ id }) => id);

	const onSelect = useCallback(
		(question) => {
			let questions = props.selectedQuestions ?? [];

			if (questions.some(({ id }) => id === question.id)) {
				questions = questions.filter(({ id }) => id !== question.id);
			} else {
				questions = questions.concat([question]);
			}

			props.onChangeSelectedQuestions?.(questions);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.selectedQuestions, props.onChangeSelectedQuestions],
	);

	return (
		<Library
			inline={props.inline}
			categories={categories}
			items={questions}
			archived={props.archived}
			placeholder={
				readOnly
					? undefined
					: {
							text: [],
							type: graphql.FeedbackQuestionType.Text,
					  }
			}
			mutations={mutations}
			scorer={{
				category() {
					return 1;
				},
				item() {
					return 1;
				},
			}}
			renderer={{
				title: (props) => (
					<Library.Category.Title
						isArchived={props.category.isArchived}
						name={props.category.name}
						query={props.query}
						active={props.active}
						loading={props.loading}
						dragging={props.dragging}
						listeners={props.listeners}
						onArchive={
							readOnly || props.category.id === '$uncategorized'
								? undefined
								: async () => {
										await archiveCategory({
											variables: { input: { id: props.category.id } },
										});

										props.onForgetCategory(props.category);
								  }
						}
						onRestore={
							readOnly || props.category.id === '$uncategorized'
								? undefined
								: async () => {
										await restoreCategory({
											variables: { input: { id: props.category.id } },
										});

										props.onForgetCategory(props.category);
								  }
						}
						onUpdate={
							readOnly
								? undefined
								: async (name) => {
										const response = await updateCategory({
											variables: { input: { id: props.category.id, name } },
										});
										const category = response.data!.updateFeedbackQuestionCategory
											.feedbackQuestionCategory as graphql.FeedbackQuestionCategory;
										props.onUpdateCategory(category);
								  }
						}
					/>
				),
				card: (props) => (
					<FeedbackQuestionCard
						{...props}
						readOnly={props.readOnly || (!props.new && !!selectedQuestionIDs)}
						selected={selectedQuestionIDs?.includes(props.item.id)}
						onSelect={props.new ? undefined : onSelect}
						buttons={
							<Button
								backgroundColor="white"
								type="destructive"
								small
								title={props.item.isArchived ? t('common.restore') : t('common.archive')}
								ml={2}
								onClick={async () => {
									await onArchive(props.item);
									props.onForget?.(props.item);
								}}
							/>
						}
					/>
				),
			}}
		/>
	);
}

export default function FeedbackLibraryPage(props: { archived?: boolean }) {
	const { t } = useTranslation();

	return (
		<Container py={4}>
			<Box px={4}>
				<Title mb={3}>{t('settings.feedbackLibrary.title')}</Title>
				<Paragraph>{t('settings.feedbackLibrary.description')}</Paragraph>

				<Box mt={2}>
					<a href={t('settings.feedbackLibrary.callout.link')} target="_blank" rel="noreferrer">
						<Callout color="blue">
							<Box>
								<strong>{t('settings.feedbackLibrary.callout.new')}:</strong>{' '}
								{t('settings.feedbackLibrary.callout.description')}
							</Box>
						</Callout>
					</a>
				</Box>
			</Box>
			<FeedbackLibrary {...props} />
		</Container>
	);
}
